// Font MIME types, as *browsers actually report them*.
//
// `File.type` comes from the browser's own extension→MIME table, and for fonts
// that table predates the `font/*` tree (RFC 8081, 2017). Chromium still maps
// `.woff` to the legacy `application/font-woff`, and on macOS the extensions it
// has no entry for at all fall through to the OS UTI database, which answers
// with vendor types like `application/x-font-ttf`. So a perfectly ordinary
// `.woff` pick arrives as `application/font-woff`, not `font/woff`.
//
// That matters because the media library identifies a font by its MIME type:
// the tile renderer, the "Copy link" action and the size limit all asked
// `type.startsWith("font/")`, which is false for every one of those aliases.
// The upload itself succeeded — the object is in the bucket and the record is in
// the library — but the tile rendered no content and the link could not be
// copied, so an uploaded font was invisible.
//
// Canonicalising instead of widening every call site also fixes the record we
// write: `typeGroup` is derived from the type (`font/woff` → `font`), so a
// normalized upload is classified correctly everywhere from the start.

// Every target below is one of the four `font/*` types the backend's upload
// allowlist permits for the `media` purpose (see services/gcp/upload-targets.js,
// FONT_TYPES). Do not map to a type that is not on that list — the signed-upload
// token is minted against it, so an un-allowlisted type is rejected at upload.
//
// A Map, not a plain object: these types are looked up from stored records, and
// on an object literal `ALIASES["constructor"]` resolves up the prototype chain
// to a truthy Function, which would leave a non-string in place of a MIME type
// and throw on the next `.startsWith`. The backend guards the same lookup the
// same way (resolveUploadTarget, services/gcp/upload-targets.js).
const FONT_MIME_ALIASES = new Map<string, string>([
  ["application/font-woff", "font/woff"],
  ["application/x-font-woff", "font/woff"],
  ["application/font-woff2", "font/woff2"],
  ["application/x-font-woff2", "font/woff2"],
  ["application/x-font-ttf", "font/ttf"],
  ["application/x-font-truetype", "font/ttf"],
  ["application/x-truetype-font", "font/ttf"],
  ["font/truetype", "font/ttf"],
  ["application/x-font-otf", "font/otf"],
  ["application/x-font-opentype", "font/otf"],
  ["application/opentype", "font/otf"],
  ["font/opentype", "font/otf"],
]);

// Embedded OpenType. Unlike the aliases above this *is* the canonical type for
// `.eot` — there is no `font/eot` — so it is recognised as a font but never
// rewritten.
const EOT_MIME_TYPE = "application/vnd.ms-fontobject";

/** Lowercased, with any `; charset=…` parameters stripped. */
const bare = (type: string | undefined | null): string =>
  typeof type === "string" ? type.split(";")[0].trim().toLowerCase() : "";

/**
 * The canonical `font/*` spelling of a font MIME type, or the type unchanged
 * when it is not a recognised font alias. Idempotent.
 */
export const normalizeFontMimeType = (type: string): string => {
  return FONT_MIME_ALIASES.get(bare(type)) ?? type;
};

/**
 * Whether a MIME type denotes a font file, tolerating the legacy and
 * vendor-prefixed spellings browsers still report.
 */
export const isFontMimeType = (type: string | undefined | null): boolean => {
  // `bare` first, so the alias lookup and the prefix test both see a
  // lowercased, parameter-free type.
  const normalized = normalizeFontMimeType(bare(type));
  return normalized.startsWith("font/") || normalized === EOT_MIME_TYPE;
};
